import { Link } from 'react-router-dom';

import { TOGGLE_IDS, useToggle } from '@/hooks/useToggles';

import DangerZoneCard from './components/DangerZoneCard';
import GiftCodeCard from './components/GiftCodeCard';
import NotificationsCard from './components/NotificationsCard';
import PreferencesCard from './components/PreferencesCard';
import ProfileInfoCard from './components/ProfileInfoCard';
import SubscriptionCard from './components/SubscriptionCard';
import TokensCard from './components/TokensCard';
import { useSubscriptionStatus } from './hooks/useCheckout';
import { useProfile } from './hooks/useProfile';
import { useTokenBalance } from './hooks/useTokens';

export default function ProfilePage() {
  const { data: profile, isLoading: profileLoading, error: profileError } = useProfile();
  const { data: balance, isLoading: balanceLoading } = useTokenBalance();
  const { data: subscription, isLoading: subscriptionLoading } = useSubscriptionStatus();

  // Feature toggles
  const subscriptionsEnabled = useToggle(TOGGLE_IDS.SUBSCRIPTIONS);
  const readingPreferencesEnabled = useToggle(TOGGLE_IDS.READING_PREFERENCES);
  const notificationPreferencesEnabled = useToggle(TOGGLE_IDS.NOTIFICATION_PREFERENCES);

  const isLoading = profileLoading || balanceLoading || subscriptionLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-pulse text-purple-300 text-xl">Loading profile...</div>
      </div>
    );
  }

  if (profileError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-4">Failed to load profile</div>
          <Link to="/" className="text-purple-400 hover:text-purple-300 underline">
            Return home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link
            to="/"
            className="p-2 -ml-2 text-purple-400 hover:text-white transition-colors"
            aria-label="Back to home"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </Link>
          <h1 className="text-xl font-semibold text-white">Profile</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {profile && <ProfileInfoCard profile={profile} />}

        <TokensCard balance={balance} />

        <GiftCodeCard />

        <SubscriptionCard subscription={subscription} enabled={subscriptionsEnabled} />

        {readingPreferencesEnabled && profile && <PreferencesCard profile={profile} />}

        {notificationPreferencesEnabled && profile && <NotificationsCard profile={profile} />}

        {profile && (
          <DangerZoneCard
            hasActiveSubscription={
              !!subscription?.hasSubscription && subscription?.status !== 'canceled'
            }
            subscriptionPlatform={subscription?.platform}
          />
        )}
      </main>
    </div>
  );
}
